import { CSVBoxButton } from "@csvbox/react"
import React, { useCallback, useRef, useState } from "react"
import styled from "styled-components"

import { sendAmplitudeData } from "../../support/amplitude-react"
import {
  csvBoxImportLooksIncomplete,
  dedupeCsvBoxRowsByEmail,
  normalizeCsvBoxClientRow
} from "../../utils/csvBoxInviteRow"
import { Button } from "../shared/Buttons"

import ClientImportProgress from "./ClientImportProgress"
import ClientImportReview from "./ClientImportReview"
import DesktopH5 from "./DesktopH5"
import Modal from "./Modal"
import Toast from "./Toast"

/** CSVBox can send multiple postMessage chunks; wait for quiet period or modal close before review. */
const CSVBOX_ACCUMULATE_MS = 1200

/**
 * Sheet email/phone validators mark rows red and disable Submit on CSVBox Verify Data.
 * Our review table is what cleans those cells. This lets Submit through with the raw values.
 */
const CSVBOX_OPTIONS = { allow_invalid: true }

const InviteBox = styled.div`
  border: 1px solid #c8d6df;
  padding: 24px;
  margin-bottom: 16px;

  i {
    margin-right: 9px;
    font-size: 18px;
  }

  .content-row {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
  }

  h4 {
    font-family: "Manrope";
    font-style: normal;
    font-weight: 700;
    font-size: 21px;
    line-height: 32px;
    letter-spacing: -0.02em;
    color: #0b3954;
  }

  @media only screen and (max-width: 768px) {
    display: none;
  }
`

export default function ClientUpload({ practice, checkImport, className, onImportSuccess }) {
  const [showAlert, setShowAlert] = useState(false)
  const [alertContent, setAlertContent] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [launchImport, setLaunchImport] = useState(false)
  const [importSuccess, setImportSuccess] = useState(false)
  const [importProgress, setImportProgress] = useState(null)
  const [reviewInitialRows, setReviewInitialRows] = useState(null)
  const [collapsedByEmail, setCollapsedByEmail] = useState(0)

  const csvBoxAccumRef = useRef({ rawRows: [], rowSuccess: 0, timer: null })
  const flushInFlightRef = useRef(false)

  const flushAccumulatedImport = useCallback(async () => {
    if (flushInFlightRef.current) return

    const raw = csvBoxAccumRef.current.rawRows
    const reportedRowSuccess = csvBoxAccumRef.current.rowSuccess
    if (csvBoxAccumRef.current.timer) {
      clearTimeout(csvBoxAccumRef.current.timer)
      csvBoxAccumRef.current.timer = null
    }
    if (!raw.length) return

    csvBoxAccumRef.current.rawRows = []
    csvBoxAccumRef.current.rowSuccess = 0

    flushInFlightRef.current = true
    const mergedRaw = dedupeCsvBoxRowsByEmail(raw)
    const reportedTotal = reportedRowSuccess > 0 ? reportedRowSuccess : raw.length

    try {
      if (csvBoxImportLooksIncomplete(raw.length, reportedRowSuccess)) {
        setShowAlert(true)
        setImportSuccess(false)
        setAlertContent(
          `Import incomplete: the file importer reported ${reportedTotal} rows but only ${raw.length} arrived in the browser (common with large files). Try again, use a smaller file, or import via Portal → Import Clients with a CSV in order: Email, First name, Last name, Phone, Notes.`
        )
        setShowModal(false)
        return
      }

      const reviewRows = mergedRaw.map((uploadedClient) => {
        const n = normalizeCsvBoxClientRow(uploadedClient)
        return {
          email: n.email.trim(),
          firstName: n.firstName,
          lastName: n.lastName,
          phone: n.phone,
          notes: n.notes
        }
      })

      setShowModal(false)
      setCollapsedByEmail(raw.length - mergedRaw.length)
      setReviewInitialRows(reviewRows)
    } finally {
      flushInFlightRef.current = false
    }
  }, [])

  const scheduleAccumulatedFlush = useCallback(() => {
    if (csvBoxAccumRef.current.timer) clearTimeout(csvBoxAccumRef.current.timer)
    csvBoxAccumRef.current.timer = setTimeout(() => {
      csvBoxAccumRef.current.timer = null
      void flushAccumulatedImport()
    }, CSVBOX_ACCUMULATE_MS)
  }, [flushAccumulatedImport])

  const handleCsvBoxClose = useCallback(() => {
    if (csvBoxAccumRef.current.timer) {
      clearTimeout(csvBoxAccumRef.current.timer)
      csvBoxAccumRef.current.timer = null
    }
    void flushAccumulatedImport()
  }, [flushAccumulatedImport])

  const handleClientImport = (result, data) => {
    if (!result) {
      console.log("Upload fail") // eslint-disable-line no-console
      return
    }
    if (!data || !Array.isArray(data.rows)) {
      return
    }

    csvBoxAccumRef.current.rawRows.push(...data.rows)
    const rs = Number(data.row_success ?? data.rowSuccess ?? 0)
    if (rs > csvBoxAccumRef.current.rowSuccess) {
      csvBoxAccumRef.current.rowSuccess = rs
    }
    scheduleAccumulatedFlush()
  }

  const handleReviewImported = ({ importedOk, inviteErrors, totalRows }) => {
    setReviewInitialRows(null)
    setShowAlert(true)
    setImportSuccess(true)
    setAlertContent(
      `${importedOk} clients imported successfully${
        inviteErrors > 0 ? `\n Could not import ${inviteErrors} clients` : ""
      }.`
    )
    sendAmplitudeData("import clients review completed", {
      "Total Rows": totalRows,
      "Imported OK": importedOk,
      "Errored Imports": inviteErrors
    })
    onImportSuccess?.()
  }

  return (
    <div className={className}>
      {importProgress && importProgress.totalRows > 0 ? (
        <div
          className="fixed inset-0 z-[20050] flex items-center justify-center bg-black/50 px-4"
          aria-busy="true"
          aria-live="polite">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl">
            <ClientImportProgress {...importProgress} />
          </div>
        </div>
      ) : null}
      <ClientImportReview
        visible={!!reviewInitialRows}
        initialRows={reviewInitialRows ?? []}
        collapsedByEmail={collapsedByEmail}
        onCancel={() => {
          setReviewInitialRows(null)
          setCollapsedByEmail(0)
        }}
        onImported={handleReviewImported}
        onImportProgress={setImportProgress}
      />
      {checkImport ? (
        <>
          <Button type="primary" size="small" onClick={() => setShowModal(true)}>
            Get started
          </Button>
          {launchImport ? (
            <CSVBoxButton
              licenseKey="XSwq17w1MiVmHnvStM7RmbA4JQvcTp"
              user={{
                user_id: practice.id
              }}
              options={CSVBOX_OPTIONS}
              onImport={handleClientImport}
              onClose={handleCsvBoxClose}
              render={(launch) => {
                setShowModal(false)
                launch()
              }}>
              Import
            </CSVBoxButton>
          ) : (
            <>
              <Modal
                open={showModal}
                closeModal={() => {
                  setShowModal(false)
                }}
                header={<DesktopH5 content="Import your clients" />}
                footer={<></>}
                size="small">
                <a onClick={() => setLaunchImport(true)}>
                  <InviteBox>
                    <div className="content-row">
                      <i className="icon-invite-clients" />
                      <h4>Import clients</h4>
                    </div>
                    Got a client list? You can upload it before booking your first appointment.
                  </InviteBox>
                </a>
                <a onClick={() => (window.location.href = "/portal/calendar#create-appointment")}>
                  <InviteBox>
                    <h4>No thanks, I&apos;m ready to book.</h4>
                  </InviteBox>
                </a>
              </Modal>
            </>
          )}
        </>
      ) : (
        <CSVBoxButton
          licenseKey="XSwq17w1MiVmHnvStM7RmbA4JQvcTp"
          user={{
            user_id: practice.id
          }}
          options={CSVBOX_OPTIONS}
          onImport={handleClientImport}
          onClose={handleCsvBoxClose}
          render={(launch) => (
            <Button type="tertiary" size="small" onClick={launch}>
              Import clients
            </Button>
          )}>
          Import
        </CSVBoxButton>
      )}
      <Toast visible={showAlert} setVisible={setShowAlert}>
        {alertContent}{" "}
        {importSuccess && <a onClick={() => (window.location.href = "/portal/clients?refresh_clients=1")}>View</a>}
      </Toast>
    </div>
  )
}
